import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import SimpleWysiwyg from 'react-simple-wysiwyg';
import { fetchTemplates, createTemplate, updateTemplate, deleteTemplate, sendTestEmail } from '../api/templatesService';

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const getColumns = (handleEditFn, handleDeleteFn, handleSendTestFn) => [
    { field: 'id', headerName: 'ID', flex: 0.5, minWidth: 90 },
    { field: 'templateType', headerName: 'Template Type', flex: 1.5, minWidth: 250 },
    {
        field: 'subject',
        headerName: 'Subject',
        flex: 2,
        minWidth: 300,
        renderCell: (params) => params.value || '(No subject)'
    },
    {
        field: 'action',
        headerName: 'Actions',
        flex: 1,
        minWidth: 200,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => handleEditFn(params.row)}
                >
                    Edit
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() => handleDeleteFn(params.row)}
                >
                    Delete
                </Button>
                <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => handleSendTestFn(params.row)}
                >
                    Send Test
                </Button>
            </Box>
        ),
    },
];

const Templates = () => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const [formData, setFormData] = useState({
        templateType: '',
        subject: '',
        body: '',
        textBody: '',
    });

    // Test email dialog state
    const [testDialogOpen, setTestDialogOpen] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [testSending, setTestSending] = useState(false);

    useEffect(() => {
        loadTemplates();
    }, []);

    const loadTemplates = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await fetchTemplates();
            setTemplates(data);
        } catch (err) {
            setError('Failed to load templates.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (template) => {
        setSelectedTemplate(template);
        setIsEditMode(true);
        setFormData({
            templateType: template.templateType || '',
            subject: template.subject || '',
            body: template.body || '',
            textBody: template.textBody || '',
        });
        setDialogOpen(true);
    };

    const handleDeleteClick = (template) => {
        setSelectedTemplate(template);
        setDeleteDialogOpen(true);
    };

    const handleOpenTestDialog = (template) => {
        setSelectedTemplate(template);
        setTestEmail('');
        setTestDialogOpen(true);
    };

    const handleCloseTestDialog = () => {
        setTestDialogOpen(false);
        setSelectedTemplate(null);
        setTestEmail('');
    };

    const handleSendTest = async () => {
        if (!selectedTemplate) return;
        if (!testEmail || !testEmail.includes('@')) {
            setSnackbar({ open: true, message: 'Please enter a valid email address.', severity: 'error' });
            return;
        }
        setTestSending(true);
        try {
            await sendTestEmail(selectedTemplate.templateType, testEmail, `Test: ${selectedTemplate.templateType}`);
            setSnackbar({ open: true, message: 'Test email sent (check inbox/spam).', severity: 'success' });
            handleCloseTestDialog();
        } catch (err) {
            console.error(err);
            const msg = err?.message || 'Failed to send test email.';
            setSnackbar({ open: true, message: msg, severity: 'error' });
        } finally {
            setTestSending(false);
        }
    };

    const handleAddNew = () => {
        setSelectedTemplate(null);
        setIsEditMode(false);
        setFormData({
            templateType: '',
            subject: '',
            body: '',
            textBody: '',
        });
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedTemplate(null);
        setIsEditMode(false);
        setFormData({
            templateType: '',
            subject: '',
            body: '',
            textBody: '',
        });
    };

    const handleDeleteDialogClose = () => {
        setDeleteDialogOpen(false);
        setSelectedTemplate(null);
    };

    const handleSave = async () => {
        if (!formData.templateType || !formData.body) {
            setSnackbar({
                open: true,
                message: 'Template Type and Body are required.',
                severity: 'error'
            });
            return;
        }

        try {
            setLoading(true);
            if (isEditMode && selectedTemplate) {
                await updateTemplate(selectedTemplate.id, {
                    ...formData,
                    id: selectedTemplate.id,
                });
                setSnackbar({
                    open: true,
                    message: 'Template updated successfully.',
                    severity: 'success'
                });
            } else {
                await createTemplate(formData);
                setSnackbar({
                    open: true,
                    message: 'Template created successfully.',
                    severity: 'success'
                });
            }
            handleDialogClose();
            loadTemplates();
        } catch (err) {
            console.error(err);
            setSnackbar({
                open: true,
                message: `Failed to ${isEditMode ? 'update' : 'create'} template.`,
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!selectedTemplate) return;

        try {
            setLoading(true);
            await deleteTemplate(selectedTemplate.id);
            setSnackbar({
                open: true,
                message: 'Template deleted successfully.',
                severity: 'success'
            });
            handleDeleteDialogClose();
            loadTemplates();
        } catch (err) {
            console.error(err);
            setSnackbar({
                open: true,
                message: 'Failed to delete template.',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleFormChange = (field) => (event) => {
        setFormData({
            ...formData,
            [field]: event.target.value,
        });
    };

    const columns = getColumns(handleEditClick, handleDeleteClick, (template) => handleOpenTestDialog(template));

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 160px)', width: '90%', margin: '40px auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h2">
                    Templates
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddNew}
                >
                    Add New Template
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Box sx={{ flex: 1, width: '100%' }}>
                <DataGrid
                    rows={templates}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    loading={loading}
                    getRowId={(row) => row.id}
                    disableColumnMenu
                    sx={{
                        height: '100%',
                        '& .MuiDataGrid-columnHeaderTitle': { display: 'block', whiteSpace: 'normal', lineHeight: 1.2, fontWeight: 700, textDecoration: 'underline' },
                        '& .MuiDataGrid-columnHeader .MuiChip-root, & .MuiDataGrid-columnHeader .MuiChip-label': { fontWeight: 400, textDecoration: 'none' },
                    }}
                />
            </Box>

            {/* Add/Edit Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleDialogClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        minHeight: '80vh'
                    }
                }}
            >
                <DialogTitle>
                    {isEditMode ? 'Edit Template' : 'Add New Template'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                        <TextField
                            label="Template Type"
                            value={formData.templateType}
                            onChange={handleFormChange('templateType')}
                            fullWidth
                            required
                            disabled={isEditMode}
                            helperText="Unique identifier for the template (e.g., r1-step1-not-complete)"
                        />
                        <TextField
                            label="Subject"
                            value={formData.subject}
                            onChange={handleFormChange('subject')}
                            fullWidth
                            multiline
                            rows={2}
                            helperText="Email subject line (optional)"
                        />
                        <Box>
                            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                                Body (HTML) - Use {`{variableName}`} for placeholders. Toggle "View HTML" to paste or edit raw HTML.
                            </Typography>
                            <Box sx={{ border: '1px solid #ccc', minHeight: '400px' }}>
                                <SimpleWysiwyg
                                    value={formData.body}
                                    onChange={e => setFormData({ ...formData, body: e.target.value })}
                                />
                            </Box>
                        </Box>
                        <TextField
                            label="Text Body (Plain Text)"
                            value={formData.textBody}
                            onChange={handleFormChange('textBody')}
                            fullWidth
                            multiline
                            rows={6}
                            helperText="Plain text version of the template (optional)"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" color="primary">
                        {isEditMode ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Send Test Dialog */}
            <Dialog open={testDialogOpen} onClose={handleCloseTestDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Send Test Email</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 1 }}>Send a quick test of the selected template to an email address. Merge fields are not populated.</Typography>
                    <TextField
                        label="Recipient Email"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        fullWidth
                        sx={{ mt: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Template: {selectedTemplate?.templateType}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseTestDialog}>Cancel</Button>
                    <Button onClick={handleSendTest} variant="contained" color="primary" disabled={testSending}>
                        {testSending ? 'Sending...' : 'Send Test'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
                <DialogTitle>Delete Template</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete template "{selectedTemplate?.templateType}"?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteDialogClose}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Templates;
